import React from "react";
import { MetricChart } from "../ChartBoard";
import type {
  ComputeDeploymentSliceResult,
  ComputeMetricId,
} from "../../models/computeDeployment";

export interface ComputeDeploymentPanelProps {
  result: ComputeDeploymentSliceResult;
  /** Controlled by the product's only first-level navigation: monitor / forecast. */
  mode: "monitor" | "forecast";
}

function formatValue(metricId: ComputeMetricId, value: number | null) {
  if (value == null) return "—";
  if (metricId === "chip_spend_usd") return `$${value.toFixed(2)}B`;
  if (metricId === "new_it_nameplate_mw") return `${value.toFixed(1)} MW`;
  return value.toFixed(1);
}

function statusLabel(status: "production-baseline" | "research-baseline") {
  return status === "production-baseline" ? "OOS 基线" : "研究基线";
}

function MethodologyDisclosure({ result }: { result: ComputeDeploymentSliceResult }) {
  return (
    <details className="sheet limitation-sheet">
      <summary className="sheet-heading compact">
        <span className="section-kicker">METHOD &amp; SOURCE</span>
        <strong>方法、边界与数据来源</strong>
      </summary>
      <div className="method-grid">
        <div><span>估计器</span><p>{result.methodology.estimator}</p></div>
        <div><span>样本外验证</span><p>{result.methodology.validation}</p></div>
        <div><span>不确定性</span><p>{result.methodology.uncertainty}</p></div>
        <div><span>INCOMPLETE 规则</span><p>{result.methodology.incompletePolicy}</p></div>
      </div>
      <div className="table-shell">
        <table className="research-table source-table">
          <caption>可追溯数据源</caption>
          <thead><tr><th>来源</th><th>更新</th><th>频率</th><th>用途</th></tr></thead>
          <tbody>{result.sources.map((source) => (
            <tr key={source.id}>
              <td><a href={source.url} target="_blank" rel="noreferrer">{source.label}</a><small>{source.license}</small></td>
              <td>{source.asOf}</td>
              <td>{source.cadence}</td>
              <td>{source.role}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </details>
  );
}

function MonitorView({ result }: { result: ComputeDeploymentSliceResult }) {
  const { monitor } = result;
  return (
    <div className="workspace-view">
      <section className="sheet" aria-labelledby="compute-monitor-heading">
        <div className="sheet-heading">
          <div>
            <div className="section-kicker">REAL-TIME DATA MONITOR</div>
            <h2 id="compute-monitor-heading">算力部署监控</h2>
            <p>实际值统一对齐至 {monitor.period}；源记录最后更新 {monitor.sourceWatermark}。</p>
          </div>
          <span className="coverage-stamp">生产路径·排除 {monitor.quality.incompleteRows} 条 incomplete</span>
        </div>
        <div className="diagnostic-strip">
          {monitor.metrics.map((metric) => (
            <div key={metric.id}>
              <span>{metric.label}</span>
              <strong>{formatValue(metric.id, metric.value)}</strong>
              <small>{formatValue(metric.id, metric.low)} — {formatValue(metric.id, metric.high)} · 覆盖 {metric.coveragePct.toFixed(0)}%</small>
            </div>
          ))}
          <div>
            <span>数据质量</span>
            <strong>{monitor.quality.productionEligibleRows}/{monitor.quality.sourceRows}</strong>
            <small>缺价 {monitor.quality.missingPriceRows} · 缺 TDP {monitor.quality.missingTdpRows}</small>
          </div>
        </div>
      </section>

      <section className="sheet" aria-labelledby="compute-designer-heading">
        <div className="sheet-heading compact">
          <div>
            <div className="section-kicker">DESIGNER DECOMPOSITION</div>
            <h3 id="compute-designer-heading">设计商拆分</h3>
            <p>“—”表示当期没有可用观测，不会被当成 0；watermark 显示该设计商最新生产可用季度。</p>
          </div>
        </div>
        <div className="table-shell">
          <table className="research-table">
            <thead><tr><th>设计商</th><th>芯片支出</th><th>交付算力指数</th><th>新增 IT MW</th><th>Watermark</th><th>不完整记录</th></tr></thead>
            <tbody>{monitor.designers.map((designer) => (
              <tr key={designer.designer}>
                <td><strong>{designer.designer}</strong><small>源更新 {designer.lastModified?.slice(0, 10) ?? "—"}</small></td>
                <td>{formatValue("chip_spend_usd", designer.metrics.chip_spend_usd)}</td>
                <td>{formatValue("delivered_compute_index", designer.metrics.delivered_compute_index)}</td>
                <td>{formatValue("new_it_nameplate_mw", designer.metrics.new_it_nameplate_mw)}</td>
                <td>{designer.watermark ?? "—"}</td>
                <td>{designer.incompleteRecords}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      {monitor.supplyOrigin ? (
        <section className="sheet" aria-labelledby="compute-origin-heading">
          <div className="sheet-heading compact">
            <div>
              <div className="section-kicker">SUPPLY ORIGIN</div>
              <h3 id="compute-origin-heading">美系 vs 中国自研供应链</h3>
              <p>按设计商总部国别（Epoch organizations.csv）把交付算力中位数加总后拆分，回答“中国自研芯片替代到什么程度”。份额基于中位数求和，未传播测量分位。</p>
            </div>
            <span className="coverage-stamp">本季中国占比 {monitor.supplyOrigin.latest.chinaSharePct.toFixed(1)}%</span>
          </div>
          <MetricChart chart={{
            label: "中国设计商占交付算力份额",
            unit: "%",
            history: monitor.supplyOrigin.series.map((point) => ({
              period: point.period,
              value: point.chinaSharePct,
              detail: `中国指数 ${point.chinaIndex.toFixed(1)} · 其他 ${point.otherIndex.toFixed(1)}`,
            })),
          }} />
          <p className="data-caveat">Huawei 与 Cambricon 的可测记录少于美系设计商；覆盖缺口会让份额短期波动，应读趋势而非单点。</p>
        </section>
      ) : null}
      <MethodologyDisclosure result={result} />
    </div>
  );
}

function ForecastView({ result }: { result: ComputeDeploymentSliceResult }) {
  const { forecast, backtest } = result;
  return (
    <div className="workspace-view">
      <section className="sheet" aria-labelledby="compute-forecast-heading">
        <div className="sheet-heading">
          <div>
            <div className="section-kicker">FORECAST / {forecast.originPeriod}</div>
            <h2 id="compute-forecast-heading">未来 1Q–4Q 算力部署路径</h2>
            <p>每个目标仅在共同滚动样本外预测点上选择基线；区间同时传递源分位和 OOS 残差。</p>
          </div>
        </div>
        <div className="table-shell">
          <table className="research-table">
            <thead><tr><th>预测期</th>{result.monitor.metrics.map((metric) => <th key={metric.id}>{metric.label}</th>)}</tr></thead>
            <tbody>{([0, 1, 2, 3] as const).map((index) => {
              const reference = forecast.paths.delivered_compute_index[index];
              return (
                <tr key={reference.period}>
                  <td><strong>{reference.period}</strong><small>{reference.horizon}Q ahead</small></td>
                  {result.monitor.metrics.map((metric) => {
                    const point = forecast.paths[metric.id][index];
                    return <td key={metric.id}>{formatValue(metric.id, point.predicted)}<small>{formatValue(metric.id, point.low)} — {formatValue(metric.id, point.high)}</small></td>;
                  })}
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </section>

      <section className="sheet" aria-labelledby="compute-backtest-heading">
        <div className="sheet-heading compact">
          <div>
            <div className="section-kicker">ROLLING-ORIGIN VALIDATION</div>
            <h3 id="compute-backtest-heading">滚动样本外回测</h3>
            <p>相同日历起点比较 lag-1、seasonal lag-4 与 drift，不用训练内拟合优势晋级。</p>
          </div>
        </div>
        <div className="table-shell">
          <table className="research-table">
            <thead><tr><th>目标</th><th>选中模型</th><th>OOS 点</th><th>RMSE</th><th>相对 lag-1</th><th>状态</th></tr></thead>
            <tbody>{result.monitor.metrics.map((metric) => {
              const validation = backtest.metrics[metric.id];
              const selected = validation.candidates.find((candidate) => candidate.id === validation.selectedModel)!;
              return (
                <tr key={metric.id} title={validation.statusReason}>
                  <td>{metric.label}</td>
                  <td>{validation.selectedModel}</td>
                  <td>{validation.observations}</td>
                  <td>{formatValue(metric.id, selected.rmse)}</td>
                  <td>{validation.skillVsLag1Pct >= 0 ? "+" : ""}{validation.skillVsLag1Pct.toFixed(1)}%</td>
                  <td><span className={`status-badge ${validation.status}`}>{statusLabel(validation.status)}</span></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </section>
      <MethodologyDisclosure result={result} />
    </div>
  );
}

/** Presentational adapter only: no network access, modeling, or local state. */
export function ComputeDeploymentPanel({ result, mode }: ComputeDeploymentPanelProps) {
  return mode === "monitor" ? <MonitorView result={result} /> : <ForecastView result={result} />;
}
